/**
 * 沖縄県の背景ツアー「沖縄めぐり」コンテンツ。
 *
 * 軸は季節。5月の守礼門から始めて、1年かけて翌年4月の竹富島へ戻る。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 首里城の正殿は復元工事中で、2026年11月23日正午まで一般公開されていない。
 *    絵にするのは、火災の影響を受けていない守礼門にする。
 *    世界遺産に登録されているのは「首里城跡」＝遺構であり、建物は復元物。この区別を崩さない。
 *  - 斎場御嶽は現在も祈りの場。「パワースポット」「願いが叶う」等の消費的な語を使わない。
 *    三庫理入口より奥は立入制限で、人物を置いた絵にしない。
 *  - 川平湾は遊泳禁止。泳ぐ絵・記述は不可。
 *  - 万座毛の「象の鼻」は公式表記では確認できない。公式の言い方（琉球石灰岩の断崖）に寄せる。
 *  - 竹富島・備瀬は住民の生活の場。敷地への立入や住宅の撮影を促さない。
 *  - 慶良間はサンゴ・ウミガメ・ザトウクジラの生息域。触れる・近づく描写をしない。
 *  - 「日本三大○○」「日本一美しい海」等の格付けは、どの公式資料でも裏が取れない。
 */

import type { StageTour } from '../types';

export const okinawaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'shureimon',
      name: '守礼門',
      area: '那覇市・5月の朝',
      image: '/assets/scenery/okinawa/scenes/01_shureimon.webp',
      hotspots: [
        {
          id: 'shurei-seiden',
          x: 0.5,
          y: 0.34,
          title: '世界遺産は「首里城跡」',
          body: '2000年、「琉球王国のグスク及び関連遺産群」として9つの城跡や建造物が世界遺産になりました。首里城跡として指定されているのは正殿の基壇遺構で、その上の正殿は平成に復元された建物でした。',
          source: 'https://oki-park.jp/shurijo/blog/detail/6931',
          mapQuery: '首里城 守礼門',
        },
        {
          id: 'shurei-fukugen',
          x: 0.24,
          y: 0.56,
          title: '正殿はいま復元工事のなか',
          body: '2019年10月31日の火災で、正殿などの主要な施設が焼失しました。2022年11月に正殿復元整備工事の起工式が行われ、2026年11月の完成が予定されています。',
          source: 'https://www8.cao.go.jp/okinawa/5/52.html',
          mapQuery: '首里城公園',
        },
      ],
    },
    {
      id: 'bise-fukugi',
      name: '備瀬のフクギ並木',
      area: '本部町・6月の午前',
      image: '/assets/scenery/okinawa/scenes/02_bise_fukugi.webp',
      hotspots: [
        {
          id: 'bise-bofurin',
          x: 0.5,
          y: 0.44,
          title: '風から家を守るための木',
          body: 'フクギには防風林として、また生垣としての役目があります。沖縄では、屋敷のまわりに防風林としてフクギを植えてきました。',
          source:
            'https://www.ogb.go.jp/kaiken/3434/kaiken_chiiki_kyodoshou/kaiken_chiiki_kyodoshou_list/014501',
          mapQuery: '備瀬のフクギ並木',
        },
        {
          id: 'bise-seikatsu',
          x: 0.76,
          y: 0.3,
          title: '250年以上、住民が守ってきた',
          body: '琉球王国時代から250年以上、備瀬の住民が守り続けてきた並木です。各家が先祖代々それぞれのフクギを手入れし、屋敷に面した道の掃除も続けてきました。',
          source: 'https://www.dc.ogb.go.jp/kaiken/3434/014501.html',
          mapQuery: '備瀬崎',
        },
      ],
    },
    {
      id: 'kabira-wan',
      name: '川平湾',
      area: '石垣市・7月の昼',
      image: '/assets/scenery/okinawa/scenes/03_kabira_wan.webp',
      hotspots: [
        {
          id: 'kabira-meisho',
          x: 0.5,
          y: 0.46,
          title: '1997年に国の名勝',
          body: '正式な指定名称は「川平湾及び於茂登岳」。1997年9月11日に国の名勝に指定されました。石垣島の西北岸で内陸に入り込んだ湾で、珊瑚礁の切れ目である狭い水路によって東シナ海に通じています。',
          source: 'https://online.bunka.go.jp/heritages/detail/140097',
          mapQuery: '川平湾',
        },
        {
          id: 'kabira-oyogenai',
          x: 0.76,
          y: 0.28,
          title: '泳ぐことはできません',
          body: '湾内は潮の流れが速いため遊泳禁止です。海の中はグラスボートから見ます。湾内では大正時代から黒真珠の養殖も行われています。',
          source: 'https://www.okinawastory.jp/spot/1377',
          mapQuery: '川平湾 グラスボート',
        },
      ],
    },
    {
      id: 'kerama',
      name: '慶良間の海',
      area: '渡嘉敷村・座間味村・8月の昼',
      image: '/assets/scenery/okinawa/scenes/04_kerama.webp',
      hotspots: [
        {
          id: 'kerama-koen',
          x: 0.5,
          y: 0.5,
          title: '2014年に国立公園になった',
          body: '平成26年3月5日に国立公園へ指定されました。公園の陸域は3,520ヘクタール、海域公園地区は8,290ヘクタールです。',
          source: 'https://www.env.go.jp/park/kerama/intro/index.html',
          mapQuery: '慶良間諸島国立公園',
        },
        {
          id: 'kerama-sango',
          x: 0.76,
          y: 0.28,
          title: 'サンゴは248種',
          body: '公園内で確認されたサンゴは248種で、日本で確認される造礁サンゴのおよそ62パーセントが、この一つの公園のなかに生きています。12月下旬から4月上旬にはザトウクジラが繁殖のためにやってきます。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/kerama-shoto/feature/story/',
          mapQuery: '座間味村',
        },
      ],
    },
    {
      id: 'kouri-ohashi',
      name: '古宇利大橋',
      area: '今帰仁村・9月の午後',
      image: '/assets/scenery/okinawa/scenes/05_kouri_ohashi.webp',
      hotspots: [
        {
          id: 'kouri-nagasa',
          x: 0.5,
          y: 0.44,
          title: '全長1,960メートル',
          body: '名護市の屋我地島と古宇利島を結ぶ離島架橋で、2005年に開通しました。沖縄本島の周辺ではもっとも長い橋として知られています。',
          source: 'https://www.nakijinson.jp/spot.php?id=22',
          mapQuery: '古宇利大橋',
        },
        {
          id: 'kouri-tenboujo',
          x: 0.76,
          y: 0.28,
          title: '南詰に展望所がある',
          body: '屋我地島側の南詰展望所に、無料の駐車場が180台分あります。橋のまわりの海は、まばゆいほどの青のグラデーションです。',
          source: 'https://www.nakijinson.jp/spot.php?id=22',
          mapQuery: '古宇利大橋南詰展望所',
        },
      ],
    },
    {
      id: 'manzamo',
      name: '万座毛',
      area: '恩納村・10月の夕',
      image: '/assets/scenery/okinawa/scenes/06_manzamo.webp',
      hotspots: [
        {
          id: 'manzamo-namae',
          x: 0.5,
          y: 0.42,
          title: '「万人が座するに足る毛（野原）」',
          body: '琉球石灰岩の断崖の上の台地に、天然の芝が広がります。1726年に琉球の国王・尚敬がそうほめたたえたことが、名前の由来とされます。',
          source: 'https://www.vill.onna.okinawa.jp/about/information/1484719566/1484720229/',
          relatedItemId: 'umibudo',
          mapQuery: '万座毛',
        },
        {
          id: 'manzamo-shokubutsu',
          x: 0.3,
          y: 0.36,
          title: '崖の上の植物は県の天然記念物',
          body: 'ハナコミカンボク、オキナワスミレ、オキナワマツバボタン、ヒメスイカズラなど、断崖上の植物群落は石灰岩層の植物として学術上重要で、県の天然記念物に指定されています。',
          source: 'https://www.okinawastory.jp/spot/1388',
          mapQuery: '万座毛周辺活性化施設',
        },
      ],
    },
    {
      id: 'zanpamisaki',
      name: '残波岬灯台',
      area: '読谷村・11月の夕',
      image: '/assets/scenery/okinawa/scenes/07_zanpamisaki.webp',
      hotspots: [
        {
          id: 'zanpa-takasa',
          x: 0.5,
          y: 0.34,
          title: '南西諸島随一の高さの灯台',
          body: '昭和49年3月30日の初点灯。地上から頂部まで31メートルで、南西諸島随一の高さです。足もとには隆起サンゴ礁の絶壁が2キロにわたって続きます。',
          source: 'https://www.tokokai.org/tourlight/tourlight14/',
          mapQuery: '残波岬灯台',
        },
        {
          id: 'zanpa-noboreru',
          x: 0.76,
          y: 0.56,
          title: '全国16基の「のぼれる灯台」のひとつ',
          body: '平均水面から灯火まで44メートル。参観寄付金は中学生以上300円、小学生以下は無料です。',
          source: 'https://www.tokokai.org/tourlight/tourlight14/',
          mapQuery: '残波岬公園',
        },
      ],
    },
    {
      id: 'nirai-kanai',
      name: 'ニライカナイ橋',
      area: '南城市・12月の夜明け',
      image: '/assets/scenery/okinawa/scenes/08_nirai_kanai.webp',
      hotspots: [
        {
          id: 'nirai-curve',
          x: 0.5,
          y: 0.46,
          title: '海へ下る大きなカーブ',
          body: '県道86号線から国道331号線へ下る途中にある、全長660メートル、高さ80メートルの橋です。海に向かって大きくカーブを描きながら下ります。',
          source: 'https://www.okinawastory.jp/spot/1323',
          mapQuery: 'ニライカナイ橋',
        },
        {
          id: 'nirai-imi',
          x: 0.76,
          y: 0.28,
          title: '「ニライカナイ」は海の向こうの理想郷',
          body: '「ニライカナイ」とは、海の向こうにある理想郷という意味です。橋からは知念岬と、青い海に浮かぶ神の島・久高島が望めます。',
          source: 'https://www.okinawastory.jp/spot/1323',
          mapQuery: 'ニライカナイ橋 展望台',
        },
      ],
    },
    {
      id: 'sefa-utaki',
      name: '斎場御嶽',
      area: '南城市・1月の朝',
      image: '/assets/scenery/okinawa/scenes/09_sefa_utaki.webp',
      // いまも祈りの場なので、祝祭的な演出を止める。
      solemn: true,
      hotspots: [
        {
          id: 'sefa-seichi',
          x: 0.36,
          y: 0.38,
          title: '琉球王国最高の聖地とされる',
          body: '琉球開闢伝説にあらわれる聖地で、創世神アマミキヨが創造したと伝えられ、六つのイビ（神域）があります。世界遺産「琉球王国のグスク及び関連遺産群」の構成資産のひとつ。',
          source: 'https://sefa.okinawa/',
          mapQuery: '斎場御嶽',
        },
        {
          id: 'sefa-manner',
          x: 0.24,
          y: 0.6,
          title: 'いまも祈りの場です',
          body: '肩や背中・お腹など、過度な肌の露出を控えた服装で訪れます。ハイヒールなどかかとの高い履物の人は、入口で無料の履物に履き替えます。動植物や石の持ち込み・持ち出しはできず、三庫理や祭壇などの立入禁止区域には入れません。',
          source: 'https://sefa.okinawa/visitors/',
          mapQuery: '南城市地域物産館',
        },
      ],
    },
    {
      id: 'taketomijima',
      name: '竹富島の集落',
      area: '竹富町・4月の昼',
      image: '/assets/scenery/okinawa/scenes/10_taketomijima.webp',
      hotspots: [
        {
          id: 'taketomi-guuku',
          x: 0.5,
          y: 0.46,
          title: 'サンゴの石を積んだ石垣',
          body: 'サンゴ礁の石を積んだ「グック」に囲まれた屋敷に、琉球赤瓦を葺いて漆喰を塗った、あるいは茅葺の木造平屋が建ちます。外壁は木の板張り、軒は深く低くつくられています。',
          source: 'https://online.bunka.go.jp/heritages/detail/148048',
          mapQuery: '竹富島',
        },
        {
          id: 'taketomi-hozon',
          x: 0.76,
          y: 0.28,
          title: '1987年に重要伝統的建造物群保存地区',
          body: '昭和62年4月28日に選定され、面積は38.3ヘクタール。屋敷の中心に居室・寝室のフーヤ（主屋）が建ち、その西隣に炊事棟のトーラが並ぶという伝統的な配置が残っています。',
          source: 'https://online.bunka.go.jp/heritages/detail/148048',
          mapQuery: '竹富島 集落',
        },
        {
          id: 'taketomi-kurashi',
          x: 0.3,
          y: 0.18,
          title: '島民の暮らしをたいせつに',
          body: '竹富島では早朝に島の方々が砂の道を整える習慣があり、朝早く訪れると道が美しく輝きます。美しい町ですが、民家の敷地をのぞいたり、ドローンで撮影したりするのはひかえ、静かな暮らしに配慮しましょう。',
          source: 'https://taketomi-shimajikan.okinawa/responsible-tourism',
          mapQuery: '竹富島 集落',
        },
      ],
    },
  ],
};
